import { EmailProvider, PlacementType } from "@/types/inboxPlacements.types";
import { isAxiosError } from "axios";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { RESTRICTED_DOMAINS } from "./constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Gets the changed values between the current form values and initial values
 * @param currentValues - Current form values
 * @param initialValues - Initial form values to compare against
 * @returns Object containing only the changed values
 */
export function getChangedFormValues<T extends Record<string, unknown>>(
  currentValues: T,
  initialValues?: Partial<T> | null
): Partial<T> {
  if (!initialValues) return currentValues;

  return Object.keys(currentValues).reduce((acc, key) => {
    const k = key as keyof T;
    const currentValue = currentValues[k];
    const initialValue = initialValues[k];

    if (currentValue !== initialValue && currentValue !== "") {
      acc[k] = currentValue;
    }
    return acc;
  }, {} as Partial<T>);
}

export function dateFormatter(date: string | Date) {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const PERSONALISATION_TOKENS = [
  "{{recipient.firstName}}",
  "{{recipient.lastName}}",
  "{{sender.firstName}}",
  "{{sender.lastName}}",
];

export function personalisationChecker(
  content: string,
  subject: string
): {
  invalidTokens: string[];
} {
  const extractTokens = (text: string): string[] =>
    text.match(/{{[^}]+}}/g) ?? [];
  const allTokens = [...extractTokens(content), ...extractTokens(subject)];

  // Analyze each token
  const tokenDetails: { token: string; isValid: boolean }[] = allTokens.map(
    (token) => ({
      token,
      isValid: PERSONALISATION_TOKENS.includes(token as string),
    })
  );

  return {
    invalidTokens: tokenDetails
      .filter((detail) => !detail.isValid)
      .map((detail) => detail.token),
  };
}

interface BlacklistRecord {
  category: string;
  host: string;
  result: string;
}

interface MXServerBlacklists {
  [serverName: string]: BlacklistRecord[];
}

interface BlacklistData {
  blacklists: {
    domain: BlacklistRecord[];
    mx: MXServerBlacklists;
    smtp: {
      [serverName: string]: BlacklistRecord[];
    };
  };
}

export interface MXServerInfo {
  server: string;
  priority: number;
  ip: string;
  blacklists: BlacklistRecord[];
}

export const blacklistData = {
  blacklists: {
    domain: [
      { category: "ivmURI", host: "growzilla.co", result: "clean" },
      { category: "Nordspam DBL", host: "growzilla.co", result: "clean" },
      { category: "SEM FRESH", host: "growzilla.co", result: "clean" },
      { category: "SEM URI", host: "growzilla.co", result: "clean" },
      { category: "SEM URIRED", host: "growzilla.co", result: "clean" },
      {
        category: "SORBS RHSBL BADCONF",
        host: "growzilla.co",
        result: "clean",
      },
      { category: "SORBS RHSBL NOMAIL", host: "growzilla.co", result: "clean" },
      { category: "Spamhaus DBL", host: "growzilla.co", result: "clean" },
      { category: "SURBL multi", host: "growzilla.co", result: "clean" },
      { category: "URIBL", host: "growzilla.co", result: "clean" },
      { category: "DBL Threatwave", host: "growzilla.co", result: "clean" },
      { category: "Spamhaus ZRD", host: "growzilla.co", result: "clean" },
    ],
    mx: {
      servers: [
        {
          server: "aspmx.l.google.com",
          priority: 1,
          ip: "142.251.167.27",
          blacklists: [
            { category: "ivmURI", host: "aspmx.l.google.com", result: "clean" },
            {
              category: "Nordspam DBL",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM FRESH",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM URI",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM URIRED",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SORBS RHSBL BADCONF",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SORBS RHSBL NOMAIL",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Spamhaus DBL",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SURBL multi",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            { category: "0SPAM", host: "aspmx.l.google.com", result: "clean" },
            {
              category: "0SPAM RBL",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Anonmails DNSBL",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BACKSCATTERER",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BARRACUDA",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BLOCKLIST.DE",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "CALIVENT",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "CYMRU BOGONS",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DAN TOR",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DAN TOREXIT",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            { category: "DRMX", host: "aspmx.l.google.com", result: "clean" },
            {
              category: "DRONE BL",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "FABELSOURCES",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            { category: "HIL", host: "aspmx.l.google.com", result: "clean" },
            { category: "HIL2", host: "aspmx.l.google.com", result: "clean" },
            {
              category: "IBM DNS Blacklist",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ICMFORBIDDEN",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IMP SPAM",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IMP WORM",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "INTERSERVER",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            { category: "ivmSIP", host: "aspmx.l.google.com", result: "clean" },
            {
              category: "ivmSIP24",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            { category: "JIPPG", host: "aspmx.l.google.com", result: "clean" },
            {
              category: "KEMPTBL",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Konstant",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "LASHBACK",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MAILSPIKE BL",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MAILSPIKE Z",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MSRBL Phishing",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MSRBL Spam",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NETHERRELAYS",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NETHERUNSURE",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NIXSPAM",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Nordspam BL",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NoSolicitado",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            { category: "PSBL", host: "aspmx.l.google.com", result: "clean" },
            {
              category: "RATS Dyna",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS NoPtr",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS Spam",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            { category: "RBL JP", host: "aspmx.l.google.com", result: "clean" },
            {
              category: "s5h.net",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SCHULTE",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM BACKSCATTER",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM BLACK",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Sender Score Reputation Network",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SERVICESNET",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SPAMCOP",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Spamhaus ZEN",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Suomispam Reputation",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            { category: "SWINOG", host: "aspmx.l.google.com", result: "clean" },
            { category: "TRIUMF", host: "aspmx.l.google.com", result: "clean" },
            {
              category: "TRUNCATE",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL1",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL2",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL3",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Woodys SMTP Blacklist",
              host: "aspmx.l.google.com",
              result: "clean",
            },
            { category: "ZapBL", host: "aspmx.l.google.com", result: "clean" },
          ],
        },
        {
          server: "alt1.aspmx.l.google.com",
          priority: 5,
          ip: "142.251.163.27",
          blacklists: [
            {
              category: "ivmURI",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Nordspam DBL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM FRESH",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM URI",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM URIRED",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SORBS RHSBL BADCONF",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SORBS RHSBL NOMAIL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Spamhaus DBL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SURBL multi",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "0SPAM",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "0SPAM RBL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Anonmails DNSBL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BACKSCATTERER",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BARRACUDA",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BLOCKLIST.DE",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "CALIVENT",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "CYMRU BOGONS",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DAN TOR",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DAN TOREXIT",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DRMX",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DRONE BL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "FABELSOURCES",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "HIL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "HIL2",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IBM DNS Blacklist",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ICMFORBIDDEN",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IMP SPAM",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IMP WORM",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "INTERSERVER",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ivmSIP",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ivmSIP24",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "JIPPG",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "KEMPTBL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Konstant",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "LASHBACK",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MAILSPIKE BL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MAILSPIKE Z",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MSRBL Phishing",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MSRBL Spam",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NETHERRELAYS",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NETHERUNSURE",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NIXSPAM",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Nordspam BL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NoSolicitado",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "PSBL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS Dyna",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS NoPtr",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS Spam",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RBL JP",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "s5h.net",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SCHULTE",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM BACKSCATTER",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM BLACK",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Sender Score Reputation Network",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SERVICESNET",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SPAMCOP",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Spamhaus ZEN",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Suomispam Reputation",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SWINOG",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "TRIUMF",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "TRUNCATE",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL1",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL2",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL3",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Woodys SMTP Blacklist",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ZapBL",
              host: "alt1.aspmx.l.google.com",
              result: "clean",
            },
          ],
        },
        {
          server: "alt2.aspmx.l.google.com",
          priority: 5,
          ip: "142.251.167.26",
          blacklists: [
            {
              category: "ivmURI",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Nordspam DBL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM FRESH",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM URI",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM URIRED",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SORBS RHSBL BADCONF",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SORBS RHSBL NOMAIL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Spamhaus DBL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SURBL multi",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "0SPAM",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "0SPAM RBL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Anonmails DNSBL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BACKSCATTERER",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BARRACUDA",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BLOCKLIST.DE",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "CALIVENT",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "CYMRU BOGONS",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DAN TOR",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DAN TOREXIT",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DRMX",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DRONE BL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "FABELSOURCES",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "HIL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "HIL2",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IBM DNS Blacklist",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ICMFORBIDDEN",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IMP SPAM",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IMP WORM",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "INTERSERVER",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ivmSIP",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ivmSIP24",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "JIPPG",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "KEMPTBL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Konstant",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "LASHBACK",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MAILSPIKE BL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MAILSPIKE Z",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MSRBL Phishing",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MSRBL Spam",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NETHERRELAYS",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NETHERUNSURE",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NIXSPAM",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Nordspam BL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NoSolicitado",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "PSBL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS Dyna",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS NoPtr",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS Spam",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RBL JP",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "s5h.net",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SCHULTE",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM BACKSCATTER",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM BLACK",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Sender Score Reputation Network",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SERVICESNET",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SPAMCOP",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Spamhaus ZEN",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Suomispam Reputation",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SWINOG",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "TRIUMF",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "TRUNCATE",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL1",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL2",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL3",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Woodys SMTP Blacklist",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ZapBL",
              host: "alt2.aspmx.l.google.com",
              result: "clean",
            },
          ],
        },
        {
          server: "alt3.aspmx.l.google.com",
          priority: 10,
          ip: "142.251.163.26",
          blacklists: [
            {
              category: "ivmURI",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Nordspam DBL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM FRESH",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM URI",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM URIRED",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SORBS RHSBL BADCONF",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SORBS RHSBL NOMAIL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Spamhaus DBL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SURBL multi",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "0SPAM",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "0SPAM RBL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Anonmails DNSBL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BACKSCATTERER",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BARRACUDA",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "BLOCKLIST.DE",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "CALIVENT",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "CYMRU BOGONS",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DAN TOR",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DAN TOREXIT",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DRMX",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "DRONE BL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "FABELSOURCES",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "HIL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "HIL2",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IBM DNS Blacklist",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ICMFORBIDDEN",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IMP SPAM",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "IMP WORM",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "INTERSERVER",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ivmSIP",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ivmSIP24",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "JIPPG",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "KEMPTBL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Konstant",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "LASHBACK",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MAILSPIKE BL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MAILSPIKE Z",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MSRBL Phishing",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "MSRBL Spam",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NETHERRELAYS",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NETHERUNSURE",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NIXSPAM",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Nordspam BL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "NoSolicitado",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "PSBL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS Dyna",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS NoPtr",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RATS Spam",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "RBL JP",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "s5h.net",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SCHULTE",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM BACKSCATTER",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SEM BLACK",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Sender Score Reputation Network",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SERVICESNET",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SPAMCOP",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Spamhaus ZEN",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Suomispam Reputation",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "SWINOG",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "TRIUMF",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "TRUNCATE",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL1",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL2",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "UCEPROTECTL3",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "Woodys SMTP Blacklist",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
            {
              category: "ZapBL",
              host: "alt3.aspmx.l.google.com",
              result: "clean",
            },
          ],
        },
      ],
    },
    smtp: {
      "smtp1.growzilla.co": [
        {
          category: "Validity SMTP",
          host: "smtp1.growzilla.co",
          result: "clean",
        },
        {
          category: "Sender Score",
          host: "smtp1.growzilla.co",
          result: "clean",
        },
        {
          category: "TrustedSource",
          host: "smtp1.growzilla.co",
          result: "clean",
        },
        {
          category: "Google Safe Browsing",
          host: "smtp1.growzilla.co",
          result: "clean",
        },
        {
          category: "Microsoft Sender ID",
          host: "smtp1.growzilla.co",
          result: "clean",
        },
        {
          category: "Return Path",
          host: "smtp1.growzilla.co",
          result: "clean",
        },
        { category: "DMARC", host: "smtp1.growzilla.co", result: "clean" },
        { category: "SPF", host: "smtp1.growzilla.co", result: "clean" },
        { category: "DKIM", host: "smtp1.growzilla.co", result: "clean" },
        {
          category: "Cisco IronPort",
          host: "smtp1.growzilla.co",
          result: "clean",
        },
        { category: "Proofpoint", host: "smtp1.growzilla.co", result: "clean" },
        { category: "Mimecast", host: "smtp1.growzilla.co", result: "clean" },
        { category: "FortiMail", host: "smtp1.growzilla.co", result: "clean" },
        {
          category: "Trend Micro",
          host: "smtp1.growzilla.co",
          result: "clean",
        },
      ],
      "smtp2.growzilla.co": [
        {
          category: "Validity SMTP",
          host: "smtp2.growzilla.co",
          result: "clean",
        },
        {
          category: "Sender Score",
          host: "smtp2.growzilla.co",
          result: "clean",
        },
        {
          category: "TrustedSource",
          host: "smtp2.growzilla.co",
          result: "clean",
        },
        {
          category: "Google Safe Browsing",
          host: "smtp2.growzilla.co",
          result: "clean",
        },
        {
          category: "Microsoft Sender ID",
          host: "smtp2.growzilla.co",
          result: "clean",
        },
        {
          category: "Return Path",
          host: "smtp2.growzilla.co",
          result: "clean",
        },
        { category: "DMARC", host: "smtp2.growzilla.co", result: "clean" },
        { category: "SPF", host: "smtp2.growzilla.co", result: "clean" },
        { category: "DKIM", host: "smtp2.growzilla.co", result: "clean" },
        {
          category: "Cisco IronPort",
          host: "smtp2.growzilla.co",
          result: "clean",
        },
        { category: "Proofpoint", host: "smtp2.growzilla.co", result: "clean" },
        { category: "Mimecast", host: "smtp2.growzilla.co", result: "clean" },
        { category: "FortiMail", host: "smtp2.growzilla.co", result: "clean" },
        {
          category: "Trend Micro",
          host: "smtp2.growzilla.co",
          result: "clean",
        },
      ],
      "smtp3.growzilla.co": [
        {
          category: "Validity SMTP",
          host: "smtp3.growzilla.co",
          result: "clean",
        },
        {
          category: "Sender Score",
          host: "smtp3.growzilla.co",
          result: "clean",
        },
        {
          category: "TrustedSource",
          host: "smtp3.growzilla.co",
          result: "clean",
        },
        {
          category: "Google Safe Browsing",
          host: "smtp3.growzilla.co",
          result: "clean",
        },
        {
          category: "Microsoft Sender ID",
          host: "smtp3.growzilla.co",
          result: "clean",
        },
        {
          category: "Return Path",
          host: "smtp3.growzilla.co",
          result: "clean",
        },
        { category: "DMARC", host: "smtp3.growzilla.co", result: "clean" },
        { category: "SPF", host: "smtp3.growzilla.co", result: "clean" },
        { category: "DKIM", host: "smtp3.growzilla.co", result: "clean" },
        {
          category: "Cisco IronPort",
          host: "smtp3.growzilla.co",
          result: "clean",
        },
        { category: "Proofpoint", host: "smtp3.growzilla.co", result: "clean" },
        { category: "Mimecast", host: "smtp3.growzilla.co", result: "clean" },
        { category: "FortiMail", host: "smtp3.growzilla.co", result: "clean" },
        {
          category: "Trend Micro",
          host: "smtp3.growzilla.co",
          result: "clean",
        },
      ],
    },
  },
};

interface EmailHop {
  delay?: string;
  from: string;
  to: string;
  provider?: "google" | "microsoft" | "other";
  protocol?: string;
  timestamp: string;
}

export const emailChain: EmailHop[] = [
  {
    to: "mx.zoho.in",
    timestamp: "2/21/2025, 9:25:04 AM GMT+5:30",
    from: "sender-q1-9.zohomail360.in",
    provider: "microsoft",
    protocol: "SMTP",
  },
  {
    delay: "4 sec",
    from: "sender-q1-9.zohomail360.in",
    to: "mx.google.com",
    provider: "google",
    protocol: "ESMTPS",
    timestamp: "2/21/2025, 9:25:08 AM GMT+5:30",
  },
  {
    to: "2002:a17:902:e54e:b0:220:d256:d133",
    from: "sender-q1-9.zohomail360.in",
    provider: "google",
    protocol: "SMTP",
    timestamp: "2/21/2025, 9:25:08 AM GMT+5:30",
  },
  {
    to: "2002:a05:6a21:1fc7:b0:1ee:31a5:ad91",
    from: "sender-q1-9.zohomail360.in",
    provider: "google",
    protocol: "SMTP",
    timestamp: "2/21/2025, 9:25:08 AM GMT+5:30",
  },
];

export const formatBytes = (bytes: number) => {
  if (bytes === 0) return "0 B";

  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const k = 1024;

  // Find the appropriate unit level
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Convert to the appropriate unit and round to 2 decimal places
  const value = bytes / Math.pow(k, i);

  // Return formatted string with unit
  return `${parseFloat(value.toFixed(2))} ${
    sizes[Math.min(i, sizes.length - 1)]
  }`;
};

export const calculateEmailSize = (html: string) => {
  const totalSize = new TextEncoder().encode(html).length;
  return formatBytes(totalSize);
};

export const isAdmin = (role: string) => role === "org:admin";
export const isMember = (role: string) => role === "org:member";

export const downloadClientSideCSV = (data: string[]) => {
  const blob = new Blob([data.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "emails.csv";
  a.click();
};

export function extractErrorMessage(
  error: unknown,
  defaultMessage: string
): string {
  if (isAxiosError(error)) {
    return error.response?.data?.message || defaultMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return defaultMessage;
}

export const allEmailProvidersDetails = {
  // --------------------- B2B Providers ---------------------
  [EmailProvider.GOOGLEWORKSPACE]: {
    id: "google-workspace",
    name: "Google Workspace",
    description: "For business emails hosted on Google Workspace",
    icon: "/providers-logos/google-workspace.png",
    type: "b2b",
    value: EmailProvider.GOOGLEWORKSPACE,
  },
  [EmailProvider.MICROSOFT365]: {
    id: "microsoft-365",
    name: "Microsoft 365",
    description: "For business emails hosted on Microsoft 365",
    icon: "/providers-logos/microsoft-365.png",
    type: "b2b",
    value: EmailProvider.MICROSOFT365,
  },
  [EmailProvider.ZOHO]: {
    id: "zoho-workmail",
    name: "Zoho Workmail",
    description: "For business emails hosted on Zoho Workmail",
    icon: "/providers-logos/zoho-mail.png",
    type: "b2b",
    value: EmailProvider.ZOHO,
  },
  [EmailProvider.WORKMAIL]: {
    id: "amazon-workmail",
    name: "Amazon Workmail",
    description: "For business emails hosted on Amazon Workmail",
    icon: "/providers-logos/aws-workmail.png",
    type: "b2b",
    value: EmailProvider.WORKMAIL,
  },
  [EmailProvider.AMAZONABUSEX]: {
    id: "abusix",
    name: "Abusix",
    description: "Enterprise Advanced Spam Filtering System",
    icon: "/providers-logos/abusix.png",
    type: "b2b",
    value: EmailProvider.AMAZONABUSEX,
  },
  [EmailProvider.AMAZONSPAMHAUS]: {
    id: "spamhaus",
    name: "Spamhaus",
    description: "Enterprise Advanced Spam Filtering System",
    icon: "/providers-logos/spamhaus.png",
    type: "b2b",
    value: EmailProvider.AMAZONSPAMHAUS,
  },
  [EmailProvider.AMAZONTRENDMICRO]: {
    id: "trend-micro",
    name: "Trend Micro",
    description: "Enterprise Advanced Virus Scanning",
    icon: "/providers-logos/trend-micro.png",
    type: "b2b",
    value: EmailProvider.AMAZONTRENDMICRO,
  },
  [EmailProvider.RSPAMD]: {
    id: "rspamd",
    name: "Rspamd",
    description: "Enterprise OpenSource Advanced Spam Filtering System",
    icon: "/providers-logos/rspamd.png",
    type: "b2b",
    value: EmailProvider.RSPAMD,
  },

  // --------------------- B2C Providers ---------------------
  [EmailProvider.GMAIL]: {
    id: "gmail",
    name: "Gmail",
    description: "For personal Gmail accounts",
    icon: "/providers-logos/gmail.png",
    type: "b2c",
    value: EmailProvider.GMAIL,
  },
  [EmailProvider.OUTLOOK]: {
    id: "outlook",
    name: "Outlook",
    description: "For personal Outlook.com accounts",
    icon: "/providers-logos/outlook.png",
    type: "b2c",
    value: EmailProvider.OUTLOOK,
  },
  [EmailProvider.AOL]: {
    id: "aol",
    name: "AOL Mail",
    description: "For personal AOL Mail accounts",
    icon: "/providers-logos/aol.png",
    type: "b2c",
    value: EmailProvider.AOL,
  },
  [EmailProvider.ICLOUD]: {
    id: "icloud",
    name: "iCloud Mail",
    description: "For personal iCloud Mail accounts",
    icon: "/providers-logos/icloud.png",
    type: "b2c",
    value: EmailProvider.ICLOUD,
  },
  [EmailProvider.GMX]: {
    id: "gmx",
    name: "GMX",
    description: "For personal GMX Mail accounts",
    icon: "/providers-logos/gmx.png",
    type: "b2c",
    value: EmailProvider.GMX,
  },
};

/** Email format only (no domain restriction). Use for reply-to, notifications, etc. */
export const validEmailFormatSchema = z
  .string()
  .min(1, "Email is required")
  .email("Please enter a valid email address");

export const validEmailSchema = z
  .string()
  .email("Please enter a valid business email address")
  .min(1, "Email is required")
  .refine(
    (email) => {
      if (email.trim() === "") {
        return false;
      }

      const domain = email.split("@")[1].toLowerCase();
      return !RESTRICTED_DOMAINS.includes(domain);
    },
    {
      message: "Please enter a valid business email address",
    }
  );
